import { MIN_ANDROID_VERSION, MIN_IOS_VERSION, MIN_MACOS_VERSION, MIN_WINDOWS_NT_VERSION } from 'constants/supportedOS';

export type OSPlatform = 'ios' | 'android' | 'windows' | 'macos' | 'other';

export const OSPlatform = {
  IOS: 'ios',
  Android: 'android',
  Windows: 'windows',
  MacOS: 'macos',
  Other: 'other',
} as const;

export const isDesktopPlatform = (platform: OSPlatform): boolean =>
  platform === OSPlatform.Windows || platform === OSPlatform.MacOS;

export interface OSInfo {
  platform: OSPlatform;
  version: number | null;
  versionString: string;
  isSupported: boolean;
  minVersion: number | null;
  minVersionString: string;
}

/**
 * Extracts iOS version from user agent.
 * Matches: "CPU iPhone OS 15_0" or "OS 15_0" (iPad)
 */
const getIOSVersion = (ua: string): number | null => {
  const match = ua.match(/OS (\d+)[_.](\d+)/);
  if (!match) return null;
  const major = parseInt(match[1], 10);
  const minor = parseInt(match[2], 10);
  return major + minor / 10;
};

/**
 * Extracts Android version from user agent.
 * Matches: "Android 10" or "Android 11"
 */
const getAndroidVersion = (ua: string): number | null => {
  const match = ua.match(/Android (\d+(?:\.\d+)?)/);
  if (!match) return null;
  return parseFloat(match[1]);
};

/** Maps Windows NT version to human-readable name */
const WINDOWS_NT_NAMES: Record<string, string> = {
  '6.1': 'Windows 7',
  '6.2': 'Windows 8',
  '6.3': 'Windows 8.1',
  '10.0': 'Windows 10/11',
};

/**
 * Extracts Windows NT version from user agent.
 * Matches: "Windows NT 10.0" or "Windows NT 6.1"
 */
const getWindowsNTVersion = (ua: string): number | null => {
  const match = ua.match(/Windows NT (\d+\.\d+)/);
  if (!match) return null;
  return parseFloat(match[1]);
};

/**
 * Extracts macOS version from user agent.
 * Matches: "Mac OS X 10_15_7" or "Intel Mac OS X 12_6_1"
 */
const getMacOSVersion = (ua: string): { version: number; versionString: string } | null => {
  const match = ua.match(/Mac OS X (\d+)_(\d+)(?:_(\d+))?/);
  if (!match) return null;
  const major = parseInt(match[1], 10);
  const minor = parseInt(match[2], 10);
  const patch = match[3] ? parseInt(match[3], 10) : 0;
  const version = major + minor / 100 + patch / 1000;
  const versionString = patch ? `${major}.${minor}.${patch}` : `${major}.${minor}`;
  return { version, versionString };
};

/**
 * Detects the user's OS platform and version from the user agent.
 * Returns whether the OS is supported and the minimum required version for that platform.
 */
export const getOSInfo = (): OSInfo => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as Window & { MSStream?: unknown }).MSStream;
  const isAndroid = /Android/.test(ua);

  if (isIOS) {
    const version = getIOSVersion(ua);
    const isSupported = version !== null && version >= MIN_IOS_VERSION;
    return {
      platform: OSPlatform.IOS,
      version,
      versionString: version !== null ? version.toFixed(1) : 'Unknown',
      isSupported,
      minVersion: MIN_IOS_VERSION,
      minVersionString: MIN_IOS_VERSION.toString(),
    };
  }

  if (isAndroid) {
    const version = getAndroidVersion(ua);
    const isSupported = version !== null && version >= MIN_ANDROID_VERSION;
    return {
      platform: OSPlatform.Android,
      version,
      versionString: version !== null ? version.toString() : 'Unknown',
      isSupported,
      minVersion: MIN_ANDROID_VERSION,
      minVersionString: `${MIN_ANDROID_VERSION}+`,
    };
  }

  if (/Windows/.test(ua)) {
    const version = getWindowsNTVersion(ua);
    const isSupported = version !== null && version >= MIN_WINDOWS_NT_VERSION;
    const versionString =
      version !== null ? (WINDOWS_NT_NAMES[version.toFixed(1)] ?? `Windows NT ${version}`) : 'Unknown';
    return {
      platform: OSPlatform.Windows,
      version,
      versionString,
      isSupported,
      minVersion: MIN_WINDOWS_NT_VERSION,
      minVersionString: 'Windows 10',
    };
  }

  if (/Macintosh|Mac OS X/.test(ua) && !/iPad|iPhone|iPod/.test(ua)) {
    const parsed = getMacOSVersion(ua);
    // Browsers cap macOS version at 10.15.7 (Chrome, Safari, Firefox) - macOS 11–15 all report 10.15.7.
    // Treat 10.15.x as supported; only warn on 10.14 (Mojave) and below.
    const isSupported = parsed === null || parsed.version >= MIN_MACOS_VERSION;
    return {
      platform: OSPlatform.MacOS,
      version: parsed?.version ?? null,
      versionString: parsed?.versionString ?? 'Unknown',
      isSupported,
      minVersion: MIN_MACOS_VERSION,
      minVersionString: 'macOS 10.15 (Catalina)',
    };
  }

  return {
    platform: OSPlatform.Other,
    version: null,
    versionString: 'N/A',
    isSupported: true, // macOS, Linux, etc. - no version check
    minVersion: null,
    minVersionString: 'N/A',
  };
};
